import { type LogtoConfig, Prompt, UserScope } from '@logto/react';
import { openLink, retrieveLaunchParams } from '@tma.js/sdk-react';

import { env } from '@/env';

// Qriots audience constants — declared before logtoConfig so they can be
// referenced in the resources array and anywhere else in this file.
const QRIOTS_ACCESS = 'qlash.qriots_access_token';
const QRIOTS_EXP = 'qlash.qriots_token_exp';
const QRIOTS_RESOURCE = 'https://qriots.qlash.gg/api';

// QBot audience — same resource as the backoffice, scoped storage keys.
const QBOT_ACCESS = 'qlash.qbot_access_token';
const QBOT_EXP = 'qlash.qbot_token_exp';
const QBOT_RESOURCE = 'https://qbot.qlash.gg';

// QSocials audience — dedicated Logto API resource for the social
// microservice (friends/follows/presence/watch/cheers).
const QSOCIALS_ACCESS = 'qlash.qsocials_access_token';
const QSOCIALS_EXP = 'qlash.qsocials_token_exp';
const QSOCIALS_RESOURCE = 'https://qsocials.qlash.gg';

// QDuels audience — Logto API resource for the 1v1 duels microservice.
// Same `/api`-suffixed audience convention as qriots; scoped storage keys.
const QDUELS_ACCESS = 'qlash.qduels_access_token';
const QDUELS_EXP = 'qlash.qduels_token_exp';
const QDUELS_RESOURCE = 'https://qduels.qlash.gg/api';

// QChess audience — Logto API resource for the chess microservice (Lichess
// tournaments + duels + QLASH leaderboard). Same `/api`-suffixed audience
// convention as qriots/qduels; scoped storage keys.
const QCHESS_ACCESS = 'qlash.qchess_access_token';
const QCHESS_EXP = 'qlash.qchess_token_exp';
const QCHESS_RESOURCE = 'https://qchess.qlash.gg/api';

// QClans audience — Logto API resource for the Clash of Clans microservice
// (stats + tracking + competitions + AI coaching). Same `/api`-suffixed
// audience convention as qriots/qduels/qchess; scoped storage keys.
const QCLANS_ACCESS = 'qlash.qclans_access_token';
const QCLANS_EXP = 'qlash.qclans_token_exp';
const QCLANS_RESOURCE = 'https://qclans.qlash.gg/api';

// QBank — virtual currency / loyalty. Logto resource = the dedicated "Qlash API
// QBank" (aud https://qbank.qlash.gg/api, resource id qreuizx76cehrbc9u287w),
// whose scopes are named like QBank's qbank:* roles (no scope->role bridge needed).
// Migrated 2026-06-02 off the legacy orphan resource https://wallet.qlash.gg.
// NB: the audience identifier is decoupled from the serving URL — the API is still
// served at https://wallet.qlash.gg (env.qbankApiUrl). In DEV the token is minted
// from QBank's internal test OIDC (see getQbankAccessToken below).
const QBANK_ACCESS = 'qlash.qbank_access_token';
const QBANK_EXP = 'qlash.qbank_token_exp';
const QBANK_RESOURCE = 'https://qbank.qlash.gg/api';

/**
 * Logto OIDC configuration for the QLASH Telegram Mini App.
 *
 * Values mirror the mobile-app feat/new-logto branch (src/services/logto.ts):
 * the same staging tenant, app, and API resource — so the access token issued
 * by Logto authenticates against the existing Laravel backend (which validates
 * via JWKS). Production values are picked up from the env when VITE_LOGTO_*
 * vars are set; defaults fall back to staging.
 */
export const logtoConfig: LogtoConfig = {
  endpoint: env.logtoEndpoint,
  appId: env.logtoAppId,
  resources: [
    env.logtoResource,
    QRIOTS_RESOURCE,
    QBOT_RESOURCE,
    QSOCIALS_RESOURCE,
    QDUELS_RESOURCE,
    QCHESS_RESOURCE,
    QCLANS_RESOURCE, // registered in Logto PROD 2026-06-01 (resource id 8j9wgxlrgykapjn90f9jz)
    QBANK_RESOURCE, // "Qlash API QBank" (id qreuizx76cehrbc9u287w); user needs qbank:wallet:read scope granted
  ],
  scopes: [
    UserScope.CustomData,
    UserScope.Email,
    UserScope.Identities,
    UserScope.Profile,
    UserScope.Roles,
    'offline_access',
  ],
  // Force re-authentication AND consent on every interactive sign-in
  // (standalone web / SDK path), mirroring `prompt=login consent` in
  // buildAuthUrl() for the Telegram bridge. `Login` re-prompts credentials
  // after a logout; `Consent` re-generates the stored grant so newly-added API
  // resources (qsocials, qduels) are authorized — otherwise their on-demand
  // refresh grant 400s and the token comes back null.
  prompt: [Prompt.Login, Prompt.Consent],
};

export const API_RESOURCE = env.logtoResource;

const SCOPE_STRING = [
  'openid',
  'offline_access',
  'profile',
  'email',
  UserScope.CustomData,
  UserScope.Identities,
  UserScope.Roles,
].join(' ');

/* ------------------------------------------------------------------ */
/* Manually-managed token storage (Telegram external-browser flow)    */
/* ------------------------------------------------------------------ */
//
// Google blocks OAuth in embedded WebViews ("disallowed_useragent"). To work
// around it we run the OIDC flow in the user's *system* browser via
// `WebApp.openLink`, then bridge the auth code back via a `t.me` deep link
// with the code in `start_param`. The mini-app then exchanges the code for
// tokens manually and stores them under these keys. RTK Query's
// `prepareHeaders` reads from this storage first, falling back to the Logto
// SDK if no manual token is present (covers the standalone web flow).

const QLASH_ACCESS = 'qlash.access_token';
const QLASH_REFRESH = 'qlash.refresh_token';
const QLASH_EXP = 'qlash.token_exp';
const PKCE_VERIFIER = 'qlash.pkce.verifier';
const PKCE_STATE = 'qlash.pkce.state';
const BRIDGE_DEBUG = 'qlash.bridge_debug';
const BACKEND_TOKEN = 'qlash.backend_token';
// Dedup key for the Telegram auth-code bridge. Owned by useLogtoAuth (which
// re-declares the same literal), exported here so clearStoredTokens can wipe it
// on logout — otherwise a stale already-consumed code survives sign-out and the
// next sign-in's consume short-circuits ("skipping already-consumed code"),
// leaving the user without fresh tokens.
export const LAST_CONSUMED_AUTH_CODE = 'qlash.last_consumed_auth_code';

export function getBridgeDebug(): string | null {
  return localStorage.getItem(BRIDGE_DEBUG);
}
export function clearBridgeDebug(): void {
  localStorage.removeItem(BRIDGE_DEBUG);
}
function dbg(msg: string): void {
  console.info('[bridge]', msg);
  const prev = localStorage.getItem(BRIDGE_DEBUG) || '';
  const stamp = new Date().toISOString().slice(11, 19);
  const next = `${prev}${stamp} ${msg}\n`;
  localStorage.setItem(BRIDGE_DEBUG, next.slice(-2000));
}

interface TokenSet {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

function persistTokens(tokens: TokenSet): void {
  localStorage.setItem(QLASH_ACCESS, tokens.access_token);
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QLASH_EXP, String(expAt));
}

export function clearStoredTokens(): void {
  localStorage.removeItem(QLASH_ACCESS);
  localStorage.removeItem(QLASH_REFRESH);
  localStorage.removeItem(QLASH_EXP);
  localStorage.removeItem(PKCE_VERIFIER);
  localStorage.removeItem(PKCE_STATE);
  localStorage.removeItem(BACKEND_TOKEN);
  localStorage.removeItem(QRIOTS_ACCESS);
  localStorage.removeItem(QRIOTS_EXP);
  localStorage.removeItem(QBOT_ACCESS);
  localStorage.removeItem(QBOT_EXP);
  localStorage.removeItem(QSOCIALS_ACCESS);
  localStorage.removeItem(QSOCIALS_EXP);
  localStorage.removeItem(QDUELS_ACCESS);
  localStorage.removeItem(QDUELS_EXP);
  localStorage.removeItem(QCHESS_ACCESS);
  localStorage.removeItem(QCHESS_EXP);
  localStorage.removeItem(QCLANS_ACCESS);
  localStorage.removeItem(QCLANS_EXP);
  localStorage.removeItem(QBANK_ACCESS);
  localStorage.removeItem(QBANK_EXP);
  // Wipe the auth-code dedup marker so the next sign-in always exchanges its
  // fresh `auth_<code>` instead of being skipped as "already consumed".
  localStorage.removeItem(LAST_CONSUMED_AUTH_CODE);
  // Reset in-flight refresh-token redemption state: a redemption that was
  // racing this logout must not resolve into freshly-cleared storage and write
  // back a dead token, and the next sign-in must start from a clean chain.
  resetRefreshState();
  // Also wipe Logto SDK's own localStorage state so the SDK re-evaluates as
  // unauthenticated on next render. Without this, `sdkAuthenticated` stays
  // true after logout and the AppShell gate continues rendering the app.
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('logto:')) localStorage.removeItem(key);
  }
}

export function hasStoredAccessToken(): boolean {
  const tok = localStorage.getItem(QLASH_ACCESS);
  const exp = parseInt(localStorage.getItem(QLASH_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

/**
 * A persisted SESSION exists if we have a still-fresh access token OR a
 * `refresh_token` we can redeem. This is what the auth gate should key off:
 * a returning user (stale access token but valid refresh_token, e.g. after a
 * reboot/refresh >1h since sign-in) is still "logged in" — the access token is
 * silently refreshed rather than bouncing them to Login. Only an explicit
 * logout clears the refresh_token (so `hasStoredSession()` → false → Login).
 */
export function hasStoredSession(): boolean {
  return hasStoredAccessToken() || !!localStorage.getItem(QLASH_REFRESH);
}

/* ------------------------------------------------------------------ */
/* PKCE helpers                                                       */
/* ------------------------------------------------------------------ */

function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes).slice(0, length);
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(buf);
}

/* ------------------------------------------------------------------ */
/* Bridge between Logto SDK and our token storage                      */
/* ------------------------------------------------------------------ */

let accessTokenGetter:
  | ((resource?: string) => Promise<string | undefined>)
  | null = null;

export function setAccessTokenGetter(
  getter: (resource?: string) => Promise<string | undefined>,
): void {
  accessTokenGetter = getter;
}

export async function getLogtoAccessToken(): Promise<string | null> {
  // Prefer manually-stored token (Telegram bridge flow).
  if (hasStoredAccessToken()) {
    return localStorage.getItem(QLASH_ACCESS);
  }
  // Stored token expired — try the refresh_token (also stored in bridge flow).
  // Refreshing with `resource` returns a fresh JWT scoped to the API resource.
  const refreshToken = localStorage.getItem(QLASH_REFRESH);
  if (refreshToken) {
    const refreshed = await refreshLogtoToken();
    if (refreshed) return refreshed;
  }
  // Fall back to Logto SDK (standalone web flow).
  if (!accessTokenGetter) return null;
  try {
    const token = await accessTokenGetter(API_RESOURCE);
    return token ?? null;
  } catch (err) {
    console.warn('[logto] getAccessToken failed:', err);
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Laravel backend token exchange                                     */
/* ------------------------------------------------------------------ */
//
// The Laravel API (api.tournaments.qlash.gg) does NOT accept Logto JWTs
// directly. Calling /tournaments with a raw Logto Bearer token returns a
// stale/cached response (looks like data from an unauthed/legacy code path).
// Mobile-app fixes this by exchanging the Logto token for a Laravel-issued
// token via POST /auth/exchange-token, then using *that* token everywhere.
// We mirror the flow here.

interface ExchangeResponse {
  access_token?: string;
  token?: string;
}

export function getBackendToken(): string | null {
  return localStorage.getItem(BACKEND_TOKEN);
}

export function clearBackendToken(): void {
  localStorage.removeItem(BACKEND_TOKEN);
}

/**
 * Refreshes the Logto access token using the stored refresh token, with the
 * `resource` parameter so Logto returns a JWT scoped to the API resource
 * (not an opaque token). Use this when the cached access_token may be opaque
 * or expired, before calling the backend exchange.
 */
async function refreshLogtoToken(): Promise<string | null> {
  // Routed through the shared serialized redemption (see redeemRefreshToken)
  // so the main/Laravel refresh can't race qriots/qbot/qsocials and rotate the
  // shared refresh_token out from under them. `redeemRefreshToken` persists the
  // rotated refresh_token centrally; we only own the main access-token cache.
  const { accessToken, expiresIn } = await redeemRefreshToken(env.logtoResource, 'logto');
  if (accessToken) {
    persistTokens({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Shared refresh-token redemption (cross-resource single-flight)      */
/* ------------------------------------------------------------------ */
//
// Logto rotates the refresh_token on EVERY `grant_type=refresh_token`
// redemption (refresh-token rotation is on for this tenant). The Mini App has
// four independent resource getters — main/Laravel (refreshLogtoToken),
// qriots, qbot, qsocials — that each read the SAME `QLASH_REFRESH` and POST it
// to redeem a resource-scoped access token. If any two run concurrently, the
// first redemption rotates R1→R2 and the second's POST of the now-dead R1
// comes back HTTP 400 invalid_grant → that getter returns null → its API slice
// sends no Authorization header → 401 "Failed to load." (with NO server-side
// JWT-verification log, because there's literally no token to verify).
//
// The Friends screen is the heaviest concurrent consumer (5 qsocials queries +
// the presence-heartbeat + profile-sync hooks from AppShell), so qsocials is
// statistically the loser of the race — but qriots/qbot/main are equally
// vulnerable; they just fire far less concurrently today.
//
// Fix: funnel ALL refresh-token redemptions through this one serialized queue.
//
//   * At most ONE refresh_token grant is in flight at any moment, across all
//     resources. A new redemption chains onto the tail of `refreshChain` and
//     only starts once the previous one settles.
//   * Each link re-reads `QLASH_REFRESH` from storage at execution time, so it
//     always redeems the LATEST rotated token (never a stale closure value).
//   * The rotated refresh_token is persisted exactly once, centrally, before
//     the link resolves — so the next link in the chain reads the fresh value.
//   * Per-resource access tokens are preserved: the grant is still made with
//     the caller's `resource`, and the resource-scoped access token is cached
//     by the caller's persist* helper (qriots/qbot/qsocials/main keys stay
//     independent). Only the underlying refresh redemption is serialized.
//   * Same-resource concurrent callers are additionally coalesced onto one
//     in-flight promise (`inFlightByResource`) so we don't redeem N times for
//     N parallel queries hitting the same API — they share one result.

interface RedeemResult {
  /** Resource-scoped access token, or null if the grant failed. */
  accessToken: string | null;
  /** Seconds until the access token expires (for the caller's exp cache). */
  expiresIn: number;
}

// Tail of the serialization chain. Every redemption awaits this before issuing
// its own network request, guaranteeing strictly sequential refresh grants.
let refreshChain: Promise<unknown> = Promise.resolve();

// Per-resource coalescing: concurrent callers for the SAME resource share one
// in-flight promise instead of enqueuing duplicate sequential redemptions.
const inFlightByResource = new Map<string, Promise<RedeemResult>>();

/**
 * Resets all in-flight refresh state. Called on logout so a redemption that
 * was racing the sign-out can't resolve into freshly-cleared storage and
 * resurrect a dead session, and so the next sign-in starts from a clean slate.
 */
export function resetRefreshState(): void {
  refreshChain = Promise.resolve();
  inFlightByResource.clear();
}

/**
 * Perform a single refresh_token redemption for `resource`, reading the latest
 * `QLASH_REFRESH` at call time and persisting the rotated refresh_token once.
 * Not exported — always reached through `redeemRefreshToken` so it stays
 * serialized. `label` only affects the warn() namespace for easier debugging.
 */
async function doRedeem(resource: string, label: string): Promise<RedeemResult> {
  const refreshToken = localStorage.getItem(QLASH_REFRESH);
  if (!refreshToken) return { accessToken: null, expiresIn: 0 };

  const tokenUrl = new URL('oidc/token', env.logtoEndpoint).toString();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: env.logtoAppId,
    resource,
  });
  try {
    const res = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) {
      console.warn(`[${label}] refresh failed`, res.status, await res.text().catch(() => ''));
      return { accessToken: null, expiresIn: 0 };
    }
    const tokens = (await res.json()) as TokenSet;
    // Persist the rotated refresh_token immediately and centrally, so the next
    // link in the chain reads the new value rather than the one we just spent.
    if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
    if (tokens.access_token) {
      return { accessToken: tokens.access_token, expiresIn: tokens.expires_in };
    }
    return { accessToken: null, expiresIn: 0 };
  } catch (err) {
    console.warn(`[${label}] refresh network error`, err);
    return { accessToken: null, expiresIn: 0 };
  }
}

/**
 * Serialized, cross-resource refresh-token redemption. ALL resource getters go
 * through here. Guarantees a single in-flight refresh_token grant at a time and
 * shares same-resource results. See the block comment above for the rationale.
 */
function redeemRefreshToken(resource: string, label: string): Promise<RedeemResult> {
  // Coalesce concurrent callers for the same resource onto one promise.
  const existing = inFlightByResource.get(resource);
  if (existing) return existing;

  // Chain onto the tail so this redemption only fires after any in-flight one
  // settles (and thus after the rotated refresh_token has been persisted).
  const run = refreshChain
    .catch(() => undefined) // a prior link's failure must not break the chain
    .then(() => doRedeem(resource, label));

  // Advance the tail. We deliberately keep the chain alive regardless of this
  // link's outcome (the .catch above guards the next link).
  refreshChain = run;

  inFlightByResource.set(resource, run);
  void run.finally(() => {
    // Only clear if we're still the in-flight entry for this resource (a newer
    // call may have replaced us after we settled).
    if (inFlightByResource.get(resource) === run) inFlightByResource.delete(resource);
  });
  return run;
}

/* ------------------------------------------------------------------ */
/* Qriots-scoped access token                                          */
/* ------------------------------------------------------------------ */
//
// Qriots-scoped access token (audience: https://qriots.qlash.gg/api).
// Cached in localStorage like the main token. Refresh on demand using
// the shared refresh_token; no separate auth flow needed.

function hasFreshQriotsToken(): boolean {
  const tok = localStorage.getItem(QRIOTS_ACCESS);
  const exp = parseInt(localStorage.getItem(QRIOTS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQriotsToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QRIOTS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QRIOTS_EXP, String(expAt));
}

/**
 * QBot-scoped access token (audience: https://qbot.qlash.gg).
 * Used by qbotApi.ts. Same caching pattern as the qriots variant.
 */
function hasFreshQbotToken(): boolean {
  const tok = localStorage.getItem(QBOT_ACCESS);
  const exp = parseInt(localStorage.getItem(QBOT_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQbotToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QBOT_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QBOT_EXP, String(expAt));
}

export async function getQbotAccessToken(): Promise<string | null> {
  if (hasFreshQbotToken()) {
    return localStorage.getItem(QBOT_ACCESS);
  }
  // Serialized redemption (shared with main/qriots/qsocials) — see
  // redeemRefreshToken. Central rotation of the refresh_token; local cache of
  // the qbot-scoped access token only.
  const { accessToken, expiresIn } = await redeemRefreshToken(QBOT_RESOURCE, 'logto-qbot');
  if (accessToken) {
    persistQbotToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/**
 * QSocials-scoped access token (audience: https://qsocials.qlash.gg).
 * Used by socialApi.ts. Same caching pattern as qriots/qbot.
 */
function hasFreshQsocialsToken(): boolean {
  const tok = localStorage.getItem(QSOCIALS_ACCESS);
  const exp = parseInt(localStorage.getItem(QSOCIALS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQsocialsToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QSOCIALS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QSOCIALS_EXP, String(expAt));
}

// The Friends screen mounts FIVE qsocials queries at once (friends, requests,
// following, followers, online-feed) plus the AppShell-level presence-heartbeat
// and profile-sync hooks. On a cold cache they all call getQsocialsAccessToken()
// in parallel. A qsocials-only single-flight guard used to dedup *those*, but it
// did NOT coordinate with the main/qriots/qbot getters, which redeem the SAME
// QLASH_REFRESH — so a concurrent main/qriots/qbot redemption could still rotate
// the refresh_token out from under qsocials (400 → null token → 401 "Failed to
// load."). That coordination now lives in redeemRefreshToken(), which serializes
// ALL resources and coalesces same-resource callers. getQsocialsAccessToken()
// just delegates to it.

export async function getQsocialsAccessToken(): Promise<string | null> {
  if (hasFreshQsocialsToken()) {
    return localStorage.getItem(QSOCIALS_ACCESS);
  }
  // Serialized + coalesced redemption (shared with main/qriots/qbot) — see
  // redeemRefreshToken. Central rotation of the refresh_token; local cache of
  // the qsocials-scoped access token only.
  const { accessToken, expiresIn } = await redeemRefreshToken(QSOCIALS_RESOURCE, 'logto-qsocials');
  if (accessToken) {
    persistQsocialsToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/**
 * Returns the user-id string used as the QSocials "userId" (Logto `sub`).
 * QSocials stores friend/follow rows keyed by the JWT sub of the
 * authenticated user. The Mini App needs the same value to know
 * "is this user me?" / build deep links to other users' profiles.
 *
 * Reads from the cached QLASH access token (the one used for the Laravel
 * API). Returns null if no token is present or decoding fails.
 */
export function getMyLogtoSub(): string | null {
  const token = localStorage.getItem(QLASH_ACCESS);
  if (!token) return null;
  const claims = decodeJwtPayload(token);
  const sub = claims?.sub;
  return typeof sub === 'string' ? sub : null;
}

/**
 * Returns an access token whose `aud` claim is `https://qriots.qlash.gg/api`.
 * Used by qriotsApi.ts. Returns null if there's no refresh_token (user
 * not signed in) or if Logto rejects the refresh (resource not authorized).
 */
export async function getQriotsAccessToken(): Promise<string | null> {
  if (hasFreshQriotsToken()) {
    return localStorage.getItem(QRIOTS_ACCESS);
  }
  // Serialized redemption (shared with main/qbot/qsocials) — see
  // redeemRefreshToken. The rotated refresh_token is persisted centrally;
  // here we only cache the qriots-scoped access token + its expiry.
  const { accessToken, expiresIn } = await redeemRefreshToken(QRIOTS_RESOURCE, 'logto-qriots');
  if (accessToken) {
    persistQriotsToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* QDuels-scoped access token                                          */
/* ------------------------------------------------------------------ */
//
// QDuels-scoped access token (audience: https://qduels.qlash.gg/api).
// Used by qduelsApi.ts to send 1v1 duel challenges from the friends list.
// Cached in localStorage like the other resources; minted on demand through
// the shared serialized refresh-token redemption (no separate auth flow).

function hasFreshQduelsToken(): boolean {
  const tok = localStorage.getItem(QDUELS_ACCESS);
  const exp = parseInt(localStorage.getItem(QDUELS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQduelsToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QDUELS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QDUELS_EXP, String(expAt));
}

/**
 * Returns an access token whose `aud` claim is `https://qduels.qlash.gg/api`.
 * Used by qduelsApi.ts. Returns null if there's no refresh_token (user not
 * signed in) or if Logto rejects the refresh — most likely because the TMA's
 * Logto app does not yet have the "Qlash API QDuels" API resource granted (in
 * which case the refresh grant returns HTTP 400 and this resolves to null).
 */
export async function getQduelsAccessToken(): Promise<string | null> {
  if (hasFreshQduelsToken()) {
    return localStorage.getItem(QDUELS_ACCESS);
  }
  // Serialized redemption (shared with main/qriots/qbot/qsocials) — see
  // redeemRefreshToken. The rotated refresh_token is persisted centrally;
  // here we only cache the qduels-scoped access token + its expiry.
  const { accessToken, expiresIn } = await redeemRefreshToken(QDUELS_RESOURCE, 'logto-qduels');
  if (accessToken) {
    persistQduelsToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* QChess-scoped access token                                          */
/* ------------------------------------------------------------------ */
//
// QChess-scoped access token (audience: https://qchess.qlash.gg/api).
// Used by qchessApi.ts for chess tournaments/duels/leaderboard. Cached in
// localStorage like the other resources; minted on demand through the shared
// serialized refresh-token redemption (no separate auth flow).

function hasFreshQchessToken(): boolean {
  const tok = localStorage.getItem(QCHESS_ACCESS);
  const exp = parseInt(localStorage.getItem(QCHESS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQchessToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QCHESS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QCHESS_EXP, String(expAt));
}

/**
 * Returns an access token whose `aud` claim is `https://qchess.qlash.gg/api`.
 * Used by qchessApi.ts. Returns null if there's no refresh_token (user not
 * signed in) or if Logto rejects the refresh — most likely because the TMA's
 * Logto app does not yet have the "Qlash API QChess" API resource granted (in
 * which case the refresh grant returns HTTP 400 and this resolves to null).
 */
export async function getQchessAccessToken(): Promise<string | null> {
  if (hasFreshQchessToken()) {
    return localStorage.getItem(QCHESS_ACCESS);
  }
  // Serialized redemption (shared with main/qriots/qbot/qsocials/qduels) — see
  // redeemRefreshToken. The rotated refresh_token is persisted centrally;
  // here we only cache the qchess-scoped access token + its expiry.
  const { accessToken, expiresIn } = await redeemRefreshToken(QCHESS_RESOURCE, 'logto-qchess');
  if (accessToken) {
    persistQchessToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* QClans-scoped access token                                          */
/* ------------------------------------------------------------------ */
//
// QClans-scoped access token (audience: https://qclans.qlash.gg/api).
// Used by qclansApi.ts for Clash of Clans stats / tracking / competitions /
// AI coaching. Cached in localStorage like the other resources; minted on
// demand through the shared serialized refresh-token redemption (no separate
// auth flow).

function hasFreshQclansToken(): boolean {
  const tok = localStorage.getItem(QCLANS_ACCESS);
  const exp = parseInt(localStorage.getItem(QCLANS_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

function persistQclansToken(tokens: TokenSet): void {
  if (tokens.refresh_token) localStorage.setItem(QLASH_REFRESH, tokens.refresh_token);
  localStorage.setItem(QCLANS_ACCESS, tokens.access_token);
  const expAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
  localStorage.setItem(QCLANS_EXP, String(expAt));
}

/**
 * Returns an access token whose `aud` claim is `https://qclans.qlash.gg/api`.
 * Used by qclansApi.ts. Returns null if there's no refresh_token (user not
 * signed in) or if Logto rejects the refresh — most likely because the TMA's
 * Logto app does not yet have the "Qlash API QClans" API resource granted (in
 * which case the refresh grant returns HTTP 400 and this resolves to null). In
 * dev that's the expected state until the resource is registered; qclansApi
 * falls back to the X-Dev-Bypass header (env.qclansDevBypass) for local
 * testing against the MacMini.
 */
export async function getQclansAccessToken(): Promise<string | null> {
  if (hasFreshQclansToken()) {
    return localStorage.getItem(QCLANS_ACCESS);
  }
  // Serialized redemption (shared with main/qriots/qbot/qsocials/qduels/qchess)
  // — see redeemRefreshToken. The rotated refresh_token is persisted centrally;
  // here we only cache the qclans-scoped access token + its expiry.
  const { accessToken, expiresIn } = await redeemRefreshToken(QCLANS_RESOURCE, 'logto-qclans');
  if (accessToken) {
    persistQclansToken({ access_token: accessToken, expires_in: expiresIn });
    return accessToken;
  }
  return null;
}

function hasFreshQbankToken(): boolean {
  const tok = localStorage.getItem(QBANK_ACCESS);
  const exp = parseInt(localStorage.getItem(QBANK_EXP) ?? '0', 10);
  return !!tok && exp - 60 > Math.floor(Date.now() / 1000);
}

/**
 * Returns a Bearer token QBank will accept.
 *
 * SPIKE (dev only): mints a token from QBank's INTERNAL test OIDC
 * (`POST /oidc/test-token`, HS256) for the configured dev sub, with the
 * `qbank:wallet:read` role. This deliberately bypasses Logto — it works only
 * while QBank runs locally with ENABLE_OIDC=true. Mirrors the qclans dev-bypass
 * idea (env-gated, dev only).
 *
 * PROD: mint a real Logto token scoped to the "Qlash API QBank" resource
 * (aud https://qbank.qlash.gg/api) via the shared serialized redeemRefreshToken —
 * same path as qriots/qbot/qclans. Our prod QBank fork validates that audience;
 * the resource scopes are named qbank:* (== the roles), so no bridge is needed.
 * Returns null (card → "—") until the user's Logto token is granted the
 * qbank:wallet:read scope (the "qbank:wallet:read" role assigned in Logto).
 */
export async function getQbankAccessToken(): Promise<string | null> {
  if (hasFreshQbankToken()) {
    return localStorage.getItem(QBANK_ACCESS);
  }
  if (env.qbankDevTestOidc && env.qbankDevSub) {
    try {
      const res = await fetch(`${env.qbankApiUrl}/oidc/test-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sub: env.qbankDevSub, roles: ['qbank:wallet:read'] }),
      });
      if (!res.ok) {
        console.warn('[qbank-dev] test-token failed', res.status);
        return null;
      }
      const data = (await res.json()) as { access_token: string; expires_in: number };
      localStorage.setItem(QBANK_ACCESS, data.access_token);
      localStorage.setItem(QBANK_EXP, String(Math.floor(Date.now() / 1000) + data.expires_in));
      return data.access_token;
    } catch (err) {
      console.warn('[qbank-dev] test-token error', err);
      return null;
    }
  }
  // PROD: real Logto token for the wallet resource via the shared serializer.
  const { accessToken, expiresIn } = await redeemRefreshToken(QBANK_RESOURCE, 'logto-qbank');
  if (accessToken) {
    localStorage.setItem(QBANK_ACCESS, accessToken);
    localStorage.setItem(QBANK_EXP, String(Math.floor(Date.now() / 1000) + expiresIn));
    return accessToken;
  }
  return null;
}

function decodeJwtPart(part: string): Record<string, unknown> | null {
  try {
    const norm = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = norm + '='.repeat((4 - (norm.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  return decodeJwtPart(parts[1]);
}

/**
 * Pull the `roles` claim from the currently-stored Logto access token.
 *
 * Logto's PROD tenant has a Custom JWT script that injects user roles into
 * every access token (see `admin-account.qlash.gg → JWT personalizzato →
 * Token di accesso utente`). For Luca that means the JWT carries something
 * like:
 *   { roles: ["user", "administrator", "backoffice", "beta", ...] }
 *
 * We consume those roles client-side because Laravel's `/users/me` does NOT
 * expose `is_admin` to mini-app callers (it gates the field behind a
 * `X-ADMIN` header used by the backoffice) and has no `beta_tester` column
 * at all — see `qlashmaker/app/Http/Resources/User/UserResource.php`.
 * Reading the token claim is the only available path until the upcoming
 * fitp-api replaces the monolith.
 *
 * Returns `[]` on decode failure or when no token is stored — callers
 * should treat absence as no privilege.
 */
export function getLogtoRolesFromAccessToken(): string[] {
  const token = localStorage.getItem(QLASH_ACCESS);
  if (!token) return [];
  const claims = decodeJwtPayload(token);
  const roles = claims?.roles;
  if (!Array.isArray(roles)) return [];
  return roles.filter((r): r is string => typeof r === 'string');
}

function decodeJwtHeader(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  return decodeJwtPart(parts[0]);
}

async function postExchange(token: string): Promise<string | null> {
  const exchangeUrl = `${env.apiUrl}/auth/exchange-token`;
  const tokenKind = token.startsWith('eyJ') ? 'JWT' : 'opaque';
  if (tokenKind === 'JWT') {
    const header = decodeJwtHeader(token);
    const claims = decodeJwtPayload(token);
    if (header) dbg(`JWT header: ${JSON.stringify(header)}`);
    if (claims) {
      dbg(`JWT payload: ${JSON.stringify(claims)}`);
    } else {
      dbg('JWT decode failed');
    }
  }
  dbg(`exchange-token POST kind=${tokenKind} len=${token.length}`);
  try {
    const res = await fetch(exchangeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        platform: 'telegram',
      },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const errBody = await res.text().catch(() => '');
      dbg(`exchange-token FAIL ${res.status} ${errBody.slice(0, 150)}`);
      return null;
    }
    const data = (await res.json()) as ExchangeResponse;
    const backendToken = data.access_token ?? data.token ?? null;
    dbg(`exchange-token OK got_token=${!!backendToken}`);
    return backendToken;
  } catch (err) {
    dbg(`exchange-token NETWORK_ERR ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}

export async function exchangeLogtoForBackend(): Promise<string | null> {
  const initial = await getLogtoAccessToken();
  if (!initial) return null;

  // First try with the cached Logto access token.
  let backendToken = await postExchange(initial);

  // If the cached token is opaque/stale, refresh it (with `resource`) to get
  // a fresh JWT, then retry the exchange.
  if (!backendToken) {
    const refreshed = await refreshLogtoToken();
    if (refreshed) backendToken = await postExchange(refreshed);
  }

  if (backendToken) {
    localStorage.setItem(BACKEND_TOKEN, backendToken);
    return backendToken;
  }
  return null;
}

/**
 * Returns the cached Laravel backend token if present, otherwise performs the
 * Logto→backend exchange and caches the result.
 */
export async function getApiAccessToken(): Promise<string | null> {
  const cached = getBackendToken();
  if (cached) return cached;
  return exchangeLogtoForBackend();
}

/* ------------------------------------------------------------------ */
/* Telegram-aware sign-in                                             */
/* ------------------------------------------------------------------ */

export function getRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/**
 * True when running inside a Telegram WebView (mobile/desktop client) — i.e.
 * `Telegram.WebApp.initData` is populated. Returns false in plain browser
 * (where the standard Logto redirect flow works).
 */
export function isTelegramWebView(): boolean {
  try {
    const lp = retrieveLaunchParams();
    return !!lp.tgWebAppData?.user;
  } catch {
    return false;
  }
}

/**
 * Builds the Logto OIDC authorize URL with PKCE state stored locally for the
 * subsequent token exchange.
 */
async function buildAuthUrl(): Promise<string> {
  const verifier = randomString(64);
  const state = randomString(32);
  const challenge = base64UrlEncode(await sha256(verifier));

  localStorage.setItem(PKCE_VERIFIER, verifier);
  localStorage.setItem(PKCE_STATE, state);

  const url = new URL('oidc/auth', env.logtoEndpoint);
  url.searchParams.set('client_id', env.logtoAppId);
  url.searchParams.set('redirect_uri', getRedirectUri());
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', SCOPE_STRING);
  // Request every resource at sign-in so the issued refresh_token is
  // authorised for all audiences. Logto allows multiple `resource` params
  // (RFC 8707); empirically verified 2026-05-22 that this does not affect
  // redirect_uri validation. Without an audience here, the on-demand refresh
  // grant for that resource (e.g. getQriotsAccessToken) returns 400.
  // NOTE (qduels): listing QDUELS_RESOURCE here only asks Logto to scope the
  // refresh_token to it — Logto still requires the "Qlash API QDuels" API
  // resource to be GRANTED to this app server-side. If that grant is missing,
  // the qduels audience is silently dropped from the token and the on-demand
  // refresh in getQduelsAccessToken() comes back 400 → null. See qduelsApi.ts.
  url.searchParams.append('resource', env.logtoResource);
  url.searchParams.append('resource', QRIOTS_RESOURCE);
  url.searchParams.append('resource', QBOT_RESOURCE);
  url.searchParams.append('resource', QSOCIALS_RESOURCE);
  url.searchParams.append('resource', QDUELS_RESOURCE);
  url.searchParams.append('resource', QCHESS_RESOURCE);
  url.searchParams.append('resource', QCLANS_RESOURCE); // resource exists in Logto PROD since 2026-06-01
  url.searchParams.append('resource', QBANK_RESOURCE); // "Qlash API QBank" (qbank.qlash.gg/api)
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  // `login consent` — two OIDC prompts, both required:
  //   • `login`   forces re-authentication (ask for credentials) every authorize
  //     instead of silently reusing an existing IdP session. REQUIRED in the
  //     Telegram bridge: sign-out runs inside the WebView and clears our local
  //     tokens, but the Logto session cookie lives in the *system browser* (a
  //     cookie jar we can't reach from the WebView). Without it, the post-logout
  //     sign-in lands on a still-alive session and skips the credential prompt.
  //   • `consent` re-generates Logto's stored consent grant (the set of
  //     authorized API resources) to include ALL currently-requested resources.
  //     Logto persists the grant per user+app and reuses it across logins, so
  //     `login` alone keeps a STALE grant — any resource added after the user's
  //     last consent (here: qsocials, qduels) is missing from it, and the
  //     on-demand refresh grant for that resource returns 400 → null token →
  //     401 "Missing Authorization header" on the qsocials/qduels calls.
  //     (First-party apps auto-consent: no extra screen is shown.)
  url.searchParams.set('prompt', 'login consent');
  return url.toString();
}

/**
 * Telegram-side sign-in: opens Logto in the user's system browser, where
 * Google/Apple OAuth aren't blocked. The bridge in <AuthCallback> then
 * deep-links back to the bot with the auth code in `start_param`.
 */
export async function startTelegramSignIn(): Promise<void> {
  const url = await buildAuthUrl();
  dbg(`signIn: openLink → ${url.slice(0, 80)}...`);
  const result = openLink.ifAvailable(url);
  dbg(`signIn: openLink.ifAvailable returned ${JSON.stringify(result)}`);
}

/**
 * Exchanges an authorization code received via `start_param` (after the
 * external-browser → t.me deep-link bridge) for tokens, and stores them.
 */
export async function exchangeAuthCode(code: string): Promise<boolean> {
  const verifier = localStorage.getItem(PKCE_VERIFIER);
  if (!verifier) {
    dbg('exchange FAIL: PKCE verifier missing');
    return false;
  }

  const tokenUrl = new URL('oidc/token', env.logtoEndpoint).toString();
  const redirectUri = getRedirectUri();
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    code_verifier: verifier,
    redirect_uri: redirectUri,
    client_id: env.logtoAppId,
    // RFC 8707 resource indicator — needed for Logto to issue a JWT scoped to
    // the API resource. Without it, access_token comes back opaque and our
    // Laravel /auth/exchange-token rejects it ("Token verification failed").
    // The qriots-scoped token is NOT requested here by design; it is minted
    // on-demand via getQriotsAccessToken() using the same refresh_token.
    resource: env.logtoResource,
  });

  dbg(`exchange POST ${tokenUrl} code=${code.slice(0, 10)}.. ver_len=${verifier.length} redir=${redirectUri}`);

  let res: Response;
  try {
    res = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
  } catch (err) {
    dbg(`exchange FAIL: network/CORS ${err instanceof Error ? err.message : String(err)}`);
    return false;
  }

  if (!res.ok) {
    const errBody = await res.text().catch(() => '<unreadable>');
    dbg(`exchange FAIL: HTTP ${res.status} ${errBody.slice(0, 200)}`);
    return false;
  }

  const tokens = (await res.json()) as TokenSet;
  if (!tokens.access_token) {
    dbg('exchange FAIL: no access_token in response');
    return false;
  }
  dbg(`exchange OK: token_len=${tokens.access_token.length} expires_in=${tokens.expires_in}`);

  // We've issued an `openid offline_access` request but Logto returns the
  // *opaque* access token only when no `resource` is specified. Since we passed
  // `resource`, this token is the JWT scoped to the API resource — exactly
  // what RTK Query needs.
  persistTokens(tokens);

  localStorage.removeItem(PKCE_VERIFIER);
  localStorage.removeItem(PKCE_STATE);
  return true;
}

/**
 * Reads the `auth_<code>` start_param when the mini-app is reopened via the
 * t.me deep-link bridge, exchanges the code for tokens, and returns true on
 * success. Returns null if no auth start_param is present.
 */
export async function consumeAuthStartParam(): Promise<boolean | null> {
  let startParam: string | undefined;
  try {
    startParam = retrieveLaunchParams().tgWebAppStartParam;
  } catch (err) {
    dbg(`consume FAIL: retrieveLaunchParams threw ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
  if (!startParam) {
    dbg('consume: no tgWebAppStartParam present');
    return null;
  }
  if (!startParam.startsWith('auth_')) {
    dbg(`consume: startParam is not auth_*: ${startParam.slice(0, 30)}`);
    return null;
  }
  const code = startParam.slice('auth_'.length);
  return await exchangeAuthCode(code);
}
